import { useNavigate, useParams } from 'react-router-dom'
import {
  AutoDepositBadge,
  ButtonVariant,
  CompanyRoleBadge,
  ProfileCompanyBadge,
  DepositWalletsDrawer,
  FallbackCompanyIcon,
  FallbackGroupIcon,
  LoadingPage,
  Company as SharedCompany,
  UserRole,
  type CompanyData,
  type CompanyStats,
  type DepositWallet,
} from '@valiant/ui-shared'
import {
  useGetCompanyQuery,
  useGetCompanyStatsQuery,
  useGetCompanyDepositWalletsQuery,
  useGetCompanyBalanceQuery,
} from '@/generated/graphql'
import { UsersTab } from '../components/tabs/UsersTab'
import { TransactionsTab } from '../components/tabs/TransactionsTab'
import { VolumesTab } from '../components/tabs/VolumesTab'
import { AddressesTab } from '../components/tabs/AddressesTab'
import { useAuth } from '@/AuthProvider'
import { toast } from 'sonner'
import { useQueryParams, type QueryParamsState } from '@valiant/ui-shared'
import { Settings, Wallet, Network } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { MainLayout } from '@/components/layout/MainLayout'

export function Company() {
  const [isWalletsDrawerOpen, setIsWalletsDrawerOpen] = useState(false)
  const { companyId } = useParams<{ companyId: string }>()
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const queryParams = useQueryParams('users')
  const navigate = useNavigate()

  const [{ data: companyData, fetching: companyLoading, error: companyError }] = useGetCompanyQuery(
    {
      variables: { companyId: companyId! },
      pause: !companyId,
    },
  )
  const [{ data: companyStatsData, fetching: companyStatsLoading, error: companyStatsError }] =
    useGetCompanyStatsQuery({
      variables: { companyId: companyId! },
      pause: !companyId,
    })
  const [{ data: walletsData, fetching: walletsLoading }, fetchWallets] =
    useGetCompanyDepositWalletsQuery({
      variables: { companyId: companyId! },
      pause: !companyId,
    })
  const [{ data: companyBalanceData }] = useGetCompanyBalanceQuery({
    variables: { companyId: companyId! },
    pause: !companyId,
  })

  // Map GraphQL data to shared component types
  const company: CompanyData | null = useMemo(
    () =>
      companyData?.getCompany
        ? {
            id: companyData.getCompany.id,
            company: companyData.getCompany.company,
            group: companyData.getCompany.group,
            groupId: companyData.getCompany.groupId,
            groupImageUrl: companyData.getCompany.groupImageUrl || undefined,
            imageUrl: companyData.getCompany.imageUrl || undefined,
            tradeType: companyData.getCompany.tradeType as
              | 'CRYPTO'
              | 'CRYPTO_AND_FIAT'
              | 'DEACTIVATED',
            autoApprove: companyData.getCompany.autoApprove,
            parentCompanyId: companyData.getCompany.parentCompanyId || undefined,
            parentCompanyName: companyData.getCompany.parentCompanyName || undefined,
          }
        : null,
    [companyData],
  )

  const companyStats: CompanyStats | null = companyStatsData?.getCompanyStats
    ? {
        flaggedTransactions: companyStatsData.getCompanyStats.flaggedTransactions,
        currentMonthlyVolumes: companyStatsData.getCompanyStats.currentMonthlyVolumes,
        previousMonthlyVolumes: companyStatsData.getCompanyStats.previousMonthlyVolumes,
        registrationNumber: companyStatsData.getCompanyStats.registrationNumber || '',
        director: companyStatsData.getCompanyStats.director || '',
        country: companyStatsData.getCompanyStats.country || '',
        address: companyStatsData.getCompanyStats.address || '',
        website: companyStatsData.getCompanyStats.website || '',
      }
    : null

  const depositWallets: DepositWallet[] = (walletsData?.getCompanyDepositWallets || []).map(
    (wallet) => ({
      currency: wallet.currency,
      address: wallet.address,
    }),
  )

  const handleFetchDepositWallets = useCallback(
    (companyId?: string) => {
      if (companyId) fetchWallets({ companyId })
    },
    [fetchWallets],
  )

  const handleCopyWalletAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Address copied to clipboard!')
  }

  const updateQueryParams = (updates: Partial<QueryParamsState>) => {
    queryParams.updateMultiple(updates)
  }

  const handleOpenWalletsDrawer = useCallback(() => {
    setIsWalletsDrawerOpen(true)
    handleFetchDepositWallets(companyId)
  }, [companyId, handleFetchDepositWallets])

  const badges = useMemo(() => {
    if (!company) return []
    const items = [
      {
        node: <CompanyRoleBadge tradeType={company?.tradeType} />,
        direction: 'LEFT' as const,
      },
      {
        node: <AutoDepositBadge autoApprove={company.autoApprove} />,
        direction: 'LEFT' as const,
      },
    ]

    if (company.parentCompanyId) {
      items.push({
        node: <ProfileCompanyBadge />,
        direction: 'LEFT',
      })
    }

    return items
  }, [company])

  const buttons = useMemo(() => {
    return [
      {
        label: 'View Org Graph',
        variant: ButtonVariant.SECONDARY,
        icon: <Network className="h-3 w-3 md:h-4 md:w-4" />,
        onClick: () => navigate(`/org-graph?companyId=${companyId}`),
      },
      {
        label: 'View Wallets',
        variant: ButtonVariant.MAIN_ACTION,
        onClick: handleOpenWalletsDrawer,
        icon: <Wallet className="h-3 w-3 md:h-4 md:w-4" />,
      },
      {
        label: 'Manage',
        variant: ButtonVariant.MAIN_ACTION,
        icon: <Settings className="h-3 w-3 md:h-4 md:w-4" />,
        onClick: () => navigate(`/companies/${companyId}/manage`),
      },
    ]
  }, [handleOpenWalletsDrawer, navigate, companyId])

  if (companyLoading || companyStatsLoading) {
    return <LoadingPage />
  }

  if (companyError || !company) {
    return (
      <MainLayout>
        <div>Company not found</div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: company.group,
          onClick: () => navigate(`/groups/${company.groupId}`),
          imageUrl: company.groupImageUrl || undefined,
          fallbackImage: <FallbackGroupIcon />,
        },
        {
          label: company.company,
          onClick: () => navigate(-1),
          imageUrl: company.imageUrl || undefined,
          fallbackImage: <FallbackCompanyIcon />,
        },
      ]}
      modalsComponent={
        <DepositWalletsDrawer
          open={isWalletsDrawerOpen}
          onOpenChange={setIsWalletsDrawerOpen}
          companyName={company.company}
          companyImageUrl={company.imageUrl}
          depositWallets={depositWallets}
          loading={walletsLoading}
          onCopyAddress={handleCopyWalletAddress}
        />
      }
      headerTitle={company.company}
      headerImage={company.imageUrl || <FallbackCompanyIcon />}
      badges={badges}
      buttons={buttons}
    >
      <SharedCompany
        companyId={companyId!}
        company={company}
        companyStats={companyStats}
        companyLoading={companyLoading}
        companyStatsLoading={companyStatsLoading}
        companyStatsError={companyStatsError}
        UsersTab={UsersTab}
        allowedRoles={[UserRole.Trader]}
        TransactionsTab={TransactionsTab}
        updateQueryParams={updateQueryParams}
        selectedTab={queryParams.tab || 'users'}
        setSelectedTab={(value) => {
          // Clear transactions filter when switching to other tabs
          if (value !== 'transactions') {
            queryParams.updateMultiple({
              tab: value,
              status: undefined,
              type: undefined,
              isFlagged: undefined,
            })
          } else {
            queryParams.updateTab(value)
          }
        }}
        AddressesTab={!isComplianceUser ? AddressesTab : undefined}
        VolumesTab={VolumesTab}
        companyBalance={companyBalanceData?.getCompanyBalance.holdings.map((holding) => ({
          currency: holding.mint.symbol,
          amount: holding.balance,
          usdAmount: holding.usdValue.replace('$', ''),
          image: holding.mint.image || '',
        }))}
        companyBalanceTotalUsdValue={
          companyBalanceData?.getCompanyBalance.totalUsdValue || '$0.00'
        }
      />
    </MainLayout>
  )
}
